'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface WatchItem {
  id?: string;
  symbol: string;
  price: number | null;
  changePct: number | null;
  alerts: number;
  hasData: boolean;
}

interface WatchlistGroup {
  id: string;
  name: string;
  items: WatchItem[];
  expanded: boolean;
}

interface WatchlistSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function WatchlistSidebar({ collapsed, onToggle }: WatchlistSidebarProps) {
  const [watchlists, setWatchlists] = useState<WatchlistGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingSymbol, setAddingSymbol] = useState(false);
  const [newSymbol, setNewSymbol] = useState('');
  const [addError, setAddError] = useState('');
  const [addingToId, setAddingToId] = useState<string | null>(null);
  const supabase = createClient();

  const fetchWatchlists = async () => {
    try {
      const res = await fetch('/api/watchlists');
      const json = await res.json();
      if (json.watchlists) {
        setWatchlists(json.watchlists.map((w: any) => ({
          id: w.id,
          name: w.name,
          expanded: w.isExpanded,
          items: w.items || [],
        })));
      }
    } catch (err) {
      // Keep state
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWatchlists();

    const channel = supabase
      .channel('watchlist_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'watchlist_items' }, () => {
        fetchWatchlists();
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'market_prices' }, () => {
        fetchWatchlists();
      })
      .subscribe();

    const pollId = setInterval(fetchWatchlists, 30000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(pollId);
    };
  }, []);

  const toggleGroup = (id: string) => {
    setWatchlists(prev => prev.map(w => w.id === id ? { ...w, expanded: !w.expanded } : w));
  };

  const handleAddSymbol = async () => {
    const sym = newSymbol.trim().toUpperCase();
    if (!sym) { setAddError('Enter a symbol'); return; }
    if (watchlists.length === 0) { setAddError('No watchlist available'); return; }

    const targetId = addingToId || watchlists[0]?.id;
    if (!targetId) { setAddError('No watchlist found'); return; }

    setAddError('');
    setAddingToId(targetId);

    try {
      const res = await fetch('/api/watchlists', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'add_symbol', watchlistId: targetId, symbol: sym }),
      });
      const json = await res.json();
      if (res.ok && json.success) {
        setNewSymbol('');
        setAddingSymbol(false);
        setAddingToId(null);
        await fetchWatchlists();
      } else {
        setAddError(json.error || 'Failed to add symbol');
      }
    } catch {
      setAddError('Network error');
    }
  };

  const handleRemoveItem = async (itemId: string) => {
    try {
      await fetch(`/api/watchlists?itemId=${itemId}`, { method: 'DELETE' });
      await fetchWatchlists();
    } catch { /* silent */ }
  };

  const totalAlerts = watchlists.flatMap(w => w.items).reduce((sum, i) => sum + i.alerts, 0);

  return (
    <aside
      className={`fixed left-0 top-[56px] bottom-0 bg-[#1E293B] border-r border-[#334155] z-20 flex flex-col transition-all duration-300 ${
        collapsed ? 'w-12' : 'w-64'
      }`}
    >
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-[#334155] shrink-0">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <Icon name="StarIcon" size={14} variant="solid" className="text-amber-500" />
            <span className="text-xs font-semibold text-slate-200 font-mono">Watchlists</span>
            {totalAlerts > 0 && (
              <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full font-mono leading-none">{totalAlerts}</span>
            )}
          </div>
        )}
        <button
          onClick={onToggle}
          className="w-7 h-7 flex items-center justify-center rounded hover:bg-[#334155] text-slate-400 hover:text-slate-200 transition-all ml-auto"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Icon name={collapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'} size={14} variant="outline" />
        </button>
      </div>

      {collapsed ? (
        <div className="flex flex-col items-center gap-3 py-3">
          <button className="w-8 h-8 flex items-center justify-center rounded hover:bg-[#334155] text-slate-400 hover:text-slate-200 transition-all" title="Watchlists">
            <Icon name="StarIcon" size={16} variant="outline" />
          </button>
          {totalAlerts > 0 && (
            <span className="w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-mono">{totalAlerts}</span>
          )}
        </div>
      ) : (
        <>
          <div className="px-3 py-2 border-b border-[#334155]">
            {addingSymbol ? (
              <div className="space-y-1.5">
                <div className="flex gap-1">
                  <input
                    type="text"
                    value={newSymbol}
                    onChange={e => { setNewSymbol(e.target.value.toUpperCase()); setAddError(''); }}
                    onKeyDown={e => { if (e.key === 'Enter') handleAddSymbol(); if (e.key === 'Escape') { setAddingSymbol(false); setNewSymbol(''); setAddError(''); } }}
                    placeholder="e.g. AAPL"
                    autoFocus
                    className="flex-1 px-2 py-1 bg-[#0F172A] border border-[#475569] rounded text-xs font-mono text-slate-200 placeholder-slate-600 focus:outline-none focus:border-sky-500"
                  />
                  <button
                    onClick={handleAddSymbol}
                    className="px-2 py-1 bg-sky-500 hover:bg-sky-400 text-white text-xs rounded transition-all"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => { setAddingSymbol(false); setNewSymbol(''); setAddError(''); }}
                    className="px-1.5 py-1 text-slate-500 hover:text-slate-300 transition-all"
                  >
                    <Icon name="XMarkIcon" size={12} variant="outline" />
                  </button>
                </div>
                {watchlists.length > 1 && (
                  <select
                    value={addingToId || watchlists[0]?.id || ''}
                    onChange={e => setAddingToId(e.target.value)}
                    className="w-full px-2 py-1 bg-[#0F172A] border border-[#475569] rounded text-xs text-slate-300 focus:outline-none"
                  >
                    {watchlists.map(w => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </select>
                )}
                {addError && <p className="text-xs text-red-400 font-mono">{addError}</p>}
              </div>
            ) : (
              <button
                onClick={() => setAddingSymbol(true)}
                className="w-full flex items-center justify-center gap-1.5 py-1.5 bg-sky-500/20 hover:bg-sky-500/30 border border-sky-500/30 rounded text-xs text-sky-400 hover:text-sky-300 transition-all min-h-[32px]"
              >
                <Icon name="PlusIcon" size={12} variant="outline" />
                Add Symbol
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto py-2">
            {loading && (
              <div className="flex items-center justify-center py-8">
                <div className="w-4 h-4 border border-sky-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {!loading && watchlists.length === 0 && (
              <div className="px-3 py-4 text-center">
                <p className="text-xs text-slate-600 font-mono">No watchlists yet</p>
              </div>
            )}

            {watchlists.map(group => (
              <div key={group.id} className="mb-1">
                <button
                  onClick={() => toggleGroup(group.id)}
                  className="w-full flex items-center justify-between px-3 py-1.5 hover:bg-[#334155]/50 transition-all"
                >
                  <div className="flex items-center gap-1.5">
                    <Icon
                      name={group.expanded ? 'ChevronDownIcon' : 'ChevronRightIcon'}
                      size={10} variant="outline" className="text-slate-500"
                    />
                    <span className="text-xs font-semibold text-slate-400 font-mono">{group.name}</span>
                  </div>
                  <span className="text-xs text-slate-600 font-mono">{group.items.length}</span>
                </button>

                {group.expanded && (
                  <div>
                    {group.items.map(item => (
                      <div key={item.symbol} className="flex items-center group">
                        <Link
                          href={`/asset-analysis-screen?symbol=${item.symbol}`}
                          className="flex-1 flex items-center justify-between px-4 py-1.5 hover:bg-[#334155]/50 transition-all"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono font-bold text-sky-400 group-hover:text-sky-300">{item.symbol}</span>
                            {item.alerts > 0 && (
                              <span className="w-4 h-4 bg-red-500/80 text-white text-xs rounded-full flex items-center justify-center font-mono leading-none">{item.alerts}</span>
                            )}
                          </div>
                          <div className="text-right">
                            {item.hasData && item.price != null ? (
                              <>
                                <p className="text-xs font-mono text-slate-300">${item.price.toFixed(2)}</p>
                                <p className={`text-xs font-mono ${(item.changePct ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                  {(item.changePct ?? 0) >= 0 ? '+' : ''}{(item.changePct ?? 0).toFixed(2)}%
                                </p>
                              </>
                            ) : (
                              <p className="text-xs font-mono text-slate-600">—</p>
                            )}
                          </div>
                        </Link>
                        {item.id && (
                          <button
                            onClick={() => handleRemoveItem(item.id!)}
                            className="px-1.5 py-1.5 text-slate-700 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                            title="Remove from watchlist"
                          >
                            <Icon name="XMarkIcon" size={10} variant="outline" />
                          </button>
                        )}
                      </div>
                    ))}
                    {group.items.length === 0 && (
                      <p className="px-4 py-2 text-xs text-slate-700 font-mono italic">Empty — add a symbol above</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="border-t border-[#334155] px-3 py-2 shrink-0">
            <Link href="/user-settings" className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition-all">
              <Icon name="Cog6ToothIcon" size={12} variant="outline" />
              Manage Watchlists
            </Link>
          </div>
        </>
      )}
    </aside>
  );
}